import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
// import { HausStory } from "@/components/HausStory";
import { FacesHero } from "@/components/FacesHero";
import { ArrowRight, ShieldCheck, Lock, Ban, Search, MessageSquare, UserPlus, MapPin, Video } from "lucide-react";

const HERO = "https://images.pexels.com/photos/6431918/pexels-photo-6431918.jpeg";

const TESTIMONIALS = [
  { q: "I stopped scrolling through hundreds of profiles. I found a producer in my genre in a week.", a: "Indie topline writer, Atlanta" },
  { q: "Three songs forced me to lead with my best work. It changed who reached out.", a: "Producer, London" },
  { q: "Finally a room that treats writers, artists and producers as equals.", a: "Recording artist, Nashville" },
];

function Stat({ value, label, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
      className="border border-line p-6 md:p-8"
    >
      <div className="font-heading text-4xl md:text-5xl font-bold text-gold tracking-tighter">{value}</div>
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ash mt-2">{label}</div>
    </motion.div>
  );
}

function CountUp({ end }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!end) return;
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / 1400);
      setN(Math.round(end * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end]);
  return <>{n}</>;
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [teaser, setTeaser] = useState([]);
  const [featured, setFeatured] = useState({ month: "", writers: [] });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/teaser").then((r) => setTeaser(r.data.profiles || [])).catch(() => {});
    api.get("/public/featured").then((r) => setFeatured(r.data || { month: "", writers: [] })).catch(() => {});
  }, []);

  const go = () => navigate(user ? "/profile" : "/auth");

  return (
    <div className="min-h-screen bg-ink">
      <Header />

      {/* HERO */}
      <section className="relative pt-16 min-h-[94vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 grid-paper opacity-50" />
          <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-40" style={{ background: "radial-gradient(circle, rgba(193,77,40,0.18) 0%, rgba(31,30,27,0) 60%)" }} />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-16 w-full grid lg:grid-cols-2 gap-8 items-center">
          {/* Copy */}
          <div className="order-1">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="font-heading text-5xl sm:text-6xl md:text-8xl font-semibold tracking-[-0.03em] leading-[0.9] text-cream"
            >
              <span className="text-gold">The home</span> for<br />indie music<br />makers.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-7 text-base md:text-lg text-ash max-w-md leading-relaxed"
            >
              Songwriters, artists & producers. This is the home to create a curated HausCard of your best three songs, an intro video, a quick bio. Connect, Share and Explore inside the Haus!
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <button
                onClick={go}
                data-testid="hero-cta-btn"
                className="group inline-flex items-center justify-center gap-3 bg-gold text-ink font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:bg-gold-hi transition-colors"
              >
                Create your free HausCard
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#featured"
                className="inline-flex items-center justify-center gap-2 border border-line text-cream font-semibold uppercase tracking-widest text-sm px-8 py-4 hover:border-gold hover:text-gold transition-colors"
              >
                Meet the writers
              </a>
            </motion.div>
            {stats?.creators > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                data-testid="join-counter"
                className="mt-7 inline-flex items-center gap-3 border border-line bg-surface/70 px-4 py-2.5"
              >
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-cream">
                  <span className="text-gold font-bold"><CountUp end={stats.creators} /></span> writers inside
                </span>
              </motion.div>
            )}
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
              className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-ash/70"
            >
              Tap a face to open their HausCard →
            </motion.p>
          </div>

          {/* 3D faces constellation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="order-2 h-[50vh] sm:h-[56vh] lg:h-[78vh] w-full"
            data-testid="faces-hero"
          >
            <FacesHero />
          </motion.div>
        </div>
      </section>

      {/* PROMO VIDEO */}
      <section className="border-t border-line" data-testid="landing-promo-section">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.6 }}>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Meet the Haus</span>
            <h2 className="font-heading text-base md:text-lg font-bold tracking-tight uppercase text-cream mt-3 mb-8 max-w-md">A word from inside — why WriterHaus exists and who it's for.</h2>
            <video
              src="/video/promo-v2.mp4"
              poster="/video/promo-poster-v2.jpg"
              controls playsInline preload="metadata"
              data-testid="landing-promo-video"
              className="w-full aspect-video bg-ink border border-line"
            />
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            <Stat value={stats ? stats.creators : "—"} label="Creators in the Haus" delay={0} />
            <Stat value={stats ? stats.songs : "—"} label="Songs shared" delay={0.05} />
            <Stat value={stats ? stats.open_to_collab : "—"} label="Open to collaborate" delay={0.1} />
            <Stat value={stats ? stats.remote : "—"} label="Remote-friendly writers" delay={0.15} />
          </div>
        </div>
      </section>

      {/* FEATURED WRITERS — 5 per month */}
      {featured.writers.length > 0 && (
        <section id="featured" className="border-t border-line scroll-mt-20">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
            <div className="flex items-baseline justify-between mb-8 flex-wrap gap-2">
              <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-cream">Featured Writers</h2>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Five for {featured.month}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {featured.writers.map((w, i) => (
                <Link
                  key={w.user_id}
                  to={`/showcase/${w.user_id}`}
                  data-testid={`featured-writer-${i}`}
                  className="group block border border-line bg-surface hover:border-gold transition-colors animate-fade-up"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="aspect-square bg-ink relative overflow-hidden">
                    {mediaUrl(w.avatar)
                      ? <img src={mediaUrl(w.avatar)} alt={w.name} className="w-full h-full object-cover media-grayscale" />
                      : <div className="w-full h-full" />}
                    <span className="absolute top-2 left-2 font-mono text-[10px] font-bold text-gold bg-ink/70 px-1.5 py-0.5">0{i + 1}</span>
                    {w.intro_video && (
                      <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-ink/80 text-cream font-mono text-[9px] px-1.5 py-0.5 uppercase tracking-wider"><Video size={9} className="text-gold" /> Intro</span>
                    )}
                  </div>
                  <div className="p-4 flex flex-col gap-2">
                    <h3 className="font-heading text-lg font-bold tracking-tight text-cream group-hover:text-gold transition-colors leading-tight">{w.name}</h3>
                    <div className="flex flex-wrap gap-1">
                      {(w.roles || []).slice(0, 1).map((r) => (
                        <span key={r} className="font-mono text-[10px] uppercase tracking-wider text-ash">{r}</span>
                      ))}
                    </div>
                    {w.location && (
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-ash/80"><MapPin size={11} />{w.location}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* TEASER GRID (blurred for visitors) */}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-cream mb-3">Inside the Haus</h2>
          <p className="text-ash mb-10 max-w-lg">A glimpse of who's here. Unlock browsing to hear the songs and request an intro.</p>
          <div className="relative">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {teaser.map((p, i) => (
                <div key={i} className="border border-line bg-surface">
                  <div className="aspect-[4/3] bg-ink overflow-hidden">
                    {mediaUrl(p.avatar)
                      ? <img src={mediaUrl(p.avatar)} alt="" className="w-full h-full object-cover media-grayscale" style={{ filter: "grayscale(100%) blur(6px)" }} />
                      : <div className="w-full h-full" />}
                  </div>
                  <div className="p-5">
                    <div className="h-5 w-2/3 bg-line mb-3" style={{ filter: "blur(3px)" }} />
                    <div className="flex flex-wrap gap-1.5">
                      {(p.roles || []).slice(0, 2).map((r) => (
                        <span key={r} className="border border-line px-2 py-0.5 font-mono text-[10px] uppercase text-ash" style={{ filter: "blur(2px)" }}>{r}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-ink/40 backdrop-blur-[2px]">
              <div className="text-center">
                <Lock size={28} className="text-gold mx-auto mb-4" />
                <p className="font-heading text-2xl font-bold text-cream mb-4 px-4">Unlock browsing to meet them</p>
                <Link to="/auth" data-testid="teaser-unlock-btn" className="inline-flex items-center gap-2 bg-gold text-ink font-bold uppercase tracking-widest text-sm px-8 py-4 hover:bg-gold-hi transition-colors">
                  Get started <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-cream mb-12">How it works</h2>
          <div className="grid md:grid-cols-3 gap-px bg-line border border-line">
            {[
              { icon: UserPlus, t: "Build your card", d: "Three songs, a bio, your images, role & genre tags. Quality over quantity — free to create." },
              { icon: Search, t: "Search the Haus", d: "Filter by role, genre, location and who's open to collaborate. $8/mo or $59/yr unlocks browsing." },
              { icon: MessageSquare, t: "Request an intro", d: "Reference the exact song you heard. They approve, a thread opens, you create together." },
            ].map((s, i) => (
              <div key={i} className="bg-surface p-8 md:p-10">
                <s.icon size={28} className="text-gold mb-6" />
                <div className="font-mono text-xs text-ash mb-2">0{i + 1}</div>
                <h3 className="font-heading text-2xl font-bold text-cream mb-3">{s.t}</h3>
                <p className="text-ash leading-relaxed text-sm">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HausStory temporarily hidden — re-enable by uncommenting */}
      {/* <HausStory /> */}

      {/* NO AI */}
      <section id="noai" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 grid md:grid-cols-[auto_1fr] gap-8 items-start">
          <Ban size={56} className="text-gold" />
          <div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight uppercase text-cream mb-4">A strict no-AI policy</h2>
            <p className="text-ash leading-relaxed max-w-2xl">
              Every song uploaded to WriterHaus requires a human-made attestation. No AI-generated music. We publish instantly and rely on the community — report and flag tools keep the Haus honest, and our takedown queue moves fast. This is a room for real people making real music.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20">
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="border border-line bg-surface p-8">
                <p className="font-heading text-xl text-cream leading-snug mb-6">“{t.q}”</p>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-gold">{t.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-line bg-cream text-ink">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 text-center">
          <ShieldCheck size={32} className="text-ink mx-auto mb-6" />
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tighter uppercase mb-6">Writers, artists, producers —<br />the home for indie music makers.</h2>
          <button onClick={go} data-testid="footer-cta-btn" className="inline-flex items-center gap-3 bg-ink text-cream font-bold uppercase tracking-widest text-sm px-10 py-5 hover:bg-gold hover:text-ink transition-colors">
            Create your free profile <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
